/**
 * FunChallengeInProgressPill
 *
 * Inline "Challenge in progress" pill that lives in the app header strip
 * (next to Download App / Raise a Ticket). Visible ONLY when:
 *   - the user has an active fun challenges battle
 *     (`funChallengesBattleStore.status === 'in_battle'`) OR an active
 *     Puzzle Royale (`funChallengesRoyaleStore.status` is 'lobby' | 'in_battle')
 *   - AND they're not currently on the corresponding battle route.
 *
 * Tapping it navigates back to the battle screen (FunChallengeBattle for 1v1,
 * PuzzleRoyaleBattle for royale). Each store's existing rejoin/hydrate plumbing
 * keeps the session state warm across route changes, so there's nothing else to
 * wire here.
 */

import React from 'react';
import {Pressable, StyleSheet, View} from 'react-native';

import {useNavigation, useNavigationState} from '@react-navigation/native';

import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import {ScreenName} from '@/navigators/StackConstants';
import useFunChallengesBattleStore from '@/Store/funChallengesBattleStore';
import useFunChallengesRoyaleStore from '@/Store/funChallengesRoyaleStore';

function getActiveRouteName(state: any): string | undefined {
  if (!state || typeof state.index !== 'number') return undefined;
  const route = state.routes?.[state.index];
  if (!route) return undefined;
  if (route.state) return getActiveRouteName(route.state);
  return route.name;
}

export default function FunChallengeInProgressPill() {
  const navigation = useNavigation<any>();
  const status = useFunChallengesBattleStore(s => s.status);
  // Puzzle Royale: an active lobby OR live race counts as "in progress" — the
  // battle screen hosts both. A spectator following a race is in-progress too.
  const royaleStatus = useFunChallengesRoyaleStore(s => s.status);
  const activeRouteName = useNavigationState(getActiveRouteName);

  const isInBattle = status === 'in_battle';
  const royaleActive =
    royaleStatus === 'lobby' || royaleStatus === 'in_battle';

  const onBattleRoute = activeRouteName === ScreenName.FunChallengeBattle;
  const onRoyaleRoute = activeRouteName === ScreenName.PuzzleRoyaleBattle;

  // Royale takes precedence if both somehow exist (royale is the newer flow).
  const target = royaleActive
    ? {active: !onRoyaleRoute, route: ScreenName.PuzzleRoyaleBattle}
    : isInBattle
    ? {active: !onBattleRoute, route: ScreenName.FunChallengeBattle}
    : {active: false, route: ScreenName.FunChallengeBattle};

  if (!target.active) return null;

  const handlePress = () => {
    try {
      navigation.navigate(target.route);
    } catch {
      // Navigation may be unmounted during a transition — non-fatal.
    }
  };

  return (
    <Pressable
      onPress={handlePress}
      style={({pressed}) => [styles.pill, pressed && styles.pillPressed]}
      accessibilityLabel="Resume your in-progress fun challenge"
      accessibilityRole="button">
      <View style={styles.dotWrap}>
        {/* Pulsing dot — purely decorative; keep static for the v1 ship to
            avoid touching Animated. Matches the existing inline pill look. */}
        <View style={styles.dot} />
      </View>
      <View style={styles.textWrap}>
        <CCText style={styles.titleText} numberOfLines={1}>
          Challenge in progress
        </CCText>
        <CCText style={styles.subText} numberOfLines={1}>
          Tap to resume
        </CCText>
      </View>
    </Pressable>
  );
}

const PILL_BG = '#FFF2D6';      // warm tint matching the yellow CTA strip
const PILL_BORDER = '#E8C988';
const TEXT_DARK = '#5C4525';
const TEXT_MUTED = '#7E6A47';
const DOT_RED = '#EB5757';

const styles = StyleSheet.create({
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: PILL_BORDER,
    backgroundColor: PILL_BG,
    marginLeft: 8,
  },
  pillPressed: {
    backgroundColor: '#FBE5B4',
  },
  dotWrap: {
    width: 10,
    height: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: DOT_RED,
  },
  textWrap: {
    flexDirection: 'column',
  },
  titleText: {
    color: TEXT_DARK,
    fontSize: 12,
    fontWeight: '700',
    lineHeight: 14,
  },
  subText: {
    color: TEXT_MUTED,
    fontSize: 10,
    lineHeight: 12,
  },
});
